/**
 * PR-D4-031 — Verbatim MadCrab_V hub Dashboard.
 * PR-D4-036 — Top-right profile badge + Exit (logout).
 * Sprint 4 Step 2 — live domain accuracy + smart training from weakest_domain.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubRwCategoryCards } from '../madcrab/HubDatabaseStats.jsx';
import { categoryTitleForKey } from '../madcrab/lib/rwCategories.js';
import { fetchWeakestVerbalSkill } from '../lib/verbal-api';
import { api } from '../lib/api-client';
import {
    avatarInitialForUser,
    displayNameForUser,
    handleLogout,
    readAuthUser,
    resolveTelemetryUserId,
} from '../lib/auth-session';
import {
    domainsForModule,
    pickSmartMathDomain,
    pickSmartVerbalDomain,
    resumePathForSession,
} from '../lib/progress';
import { DashboardRecentSessions } from '../components/DashboardRecentSessions';
import type { ActiveProgressSession, MathDomain, ProgressDomainAccuracy, ProgressSummary } from '../lib/types';
import '../madcrab/App.css';
import './Dashboard.css';

const DIFFICULTY_LABEL = {
    easy: 'Foundation (Module 1)',
    medium: 'Advanced (Module 2)',
    hard: 'Maximum difficulty',
} as const;

const MATH_SECTIONS = [
    { id: 'smart_practice', title: 'Умная тренировка — слабые категории' },
    { id: 'algebra', title: 'Algebra' },
    { id: 'advanced_math', title: 'Advanced Math' },
    { id: 'psda', title: 'Problem-Solving and Data Analysis' },
    { id: 'geometry', title: 'Geometry and Trigonometry' },
] as const;

function DomainAccuracyList({
    title,
    overallAccuracy,
    totalQuestions,
    domains,
}: {
    title: string;
    overallAccuracy: number | null;
    totalQuestions: number;
    domains: ProgressDomainAccuracy[];
}) {
    return (
        <section className="dashboard-accuracy" aria-label={title}>
            <div className="dashboard-accuracy__header">
                <h3 className="dashboard-accuracy__title">{title}</h3>
                {overallAccuracy != null ? (
                    <span className="dashboard-accuracy__overall">
                        {overallAccuracy}% · {totalQuestions} q
                    </span>
                ) : null}
            </div>
            {domains.length === 0 ? (
                <p className="dashboard-accuracy__empty">No attempts yet — start a section to see accuracy.</p>
            ) : (
                <ul className="dashboard-accuracy__list">
                    {domains.map((row) => {
                        const tone =
                            row.accuracy >= 80 ? 'strong' : row.accuracy < 60 ? 'weak' : 'mid';
                        return (
                            <li key={row.domain} className="dashboard-accuracy__row">
                                <div className="dashboard-accuracy__meta">
                                    <span>{row.label}</span>
                                    <span>
                                        {row.accuracy}% ({row.correct}/{row.attempts})
                                    </span>
                                </div>
                                <div className="dashboard-accuracy__track" aria-hidden>
                                    <div
                                        className={`dashboard-accuracy__fill${
                                            tone === 'strong'
                                                ? ' dashboard-accuracy__fill--strong'
                                                : tone === 'weak'
                                                  ? ' dashboard-accuracy__fill--weak'
                                                  : ''
                                        }`}
                                        style={{ width: `${Math.min(100, Math.max(0, row.accuracy))}%` }}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function ActiveSessionBanner({
    session,
    onResume,
    onReset,
}: {
    session: ActiveProgressSession;
    onResume: () => void;
    onReset: () => void;
}) {
    const total = session.questionsOrder.length || session.questions.length;
    const domainLabel = (session.domain || session.moduleType).replace(/_/g, ' ');
    return (
        <section
            className="dashboard-resume w-full flex flex-col sm:flex-row space-y-2 sm:space-y-0"
            aria-label="Unfinished test"
        >
            <p>
                У вас есть незавершенный тест по {domainLabel} (Вопрос{' '}
                {session.currentIndex + 1} из {total || '?'})
            </p>
            <div className="dashboard-resume__actions w-full flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button type="button" className="app-hub-primary" onClick={onResume}>
                    Продолжить тест
                </button>
                <button type="button" className="dashboard-resume__reset" onClick={onReset}>
                    Сбросить
                </button>
            </div>
        </section>
    );
}

export function Dashboard() {
    const navigate = useNavigate();
    const user = useMemo(() => readAuthUser(), []);
    const studentId = resolveTelemetryUserId();
    const [rwDifficulty, setRwDifficulty] = useState('foundation');
    const [mathDifficulty, setMathDifficulty] = useState('foundation');
    const [smartDrillBusy, setSmartDrillBusy] = useState(false);
    const [smartDrillHint, setSmartDrillHint] = useState('');
    const [mathSmartBusy, setMathSmartBusy] = useState(false);
    const [mathSmartHint, setMathSmartHint] = useState('');
    const [avatarFailed, setAvatarFailed] = useState(false);
    const [summary, setSummary] = useState<ProgressSummary | null>(null);
    const [activeSession, setActiveSession] = useState<ActiveProgressSession | null>(null);

    const reloadActiveSession = useCallback(async () => {
        try {
            const payload = await api.getActiveProgressSession();
            setActiveSession(payload.session);
        } catch {
            setActiveSession(null);
        }
    }, []);

    useEffect(() => {
        void reloadActiveSession();
        const onFocus = () => {
            void reloadActiveSession();
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') void reloadActiveSession();
        };
        window.addEventListener('pageshow', onFocus);
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('pageshow', onFocus);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [reloadActiveSession]);

    const reloadSummary = useCallback(async () => {
        try {
            setSummary(await api.getProgressSummary(studentId));
        } catch {
            setSummary(null);
        }
    }, [studentId]);

    useEffect(() => {
        let cancelled = false;
        const load = () => {
            void api
                .getProgressSummary(studentId)
                .then((data) => {
                    if (!cancelled) setSummary(data);
                })
                .catch(() => {
                    if (!cancelled) setSummary(null);
                });
        };
        load();
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            cancelled = true;
            window.removeEventListener('focus', onFocus);
        };
    }, [studentId]);

    const handleDeleteSession = useCallback(
        async (sessionId: string) => {
            await api.deleteSession(sessionId);
            await reloadSummary();
        },
        [reloadSummary],
    );

    const resumeDraft = useCallback(() => {
        if (!activeSession) return;
        navigate(resumePathForSession(activeSession));
    }, [activeSession, navigate]);

    const resetDraft = useCallback(async () => {
        if (!activeSession) return;
        try {
            await api.abandonProgressSession(activeSession.sessionId);
        } catch {
            // Banner still clears locally so the student can start a new test.
        }
        setActiveSession(null);
    }, [activeSession]);

    const beginSectionTest = useCallback(
        (sectionKey: string, _sectionTitle: string, difficulty: string, smart = false) => {
            const params = new URLSearchParams({
                mode: 'training',
                studentId,
                difficulty,
                topic: `verbal/${sectionKey}`,
            });
            if (smart) params.set('smart', '1');
            navigate(`/test?${params}`);
        },
        [navigate, studentId],
    );

    const beginMathSection = useCallback(
        (domain: string, smart = false) => {
            const params = new URLSearchParams({
                domain,
                difficulty: mathDifficulty,
                studentId,
            });
            if (smart) params.set('smart', '1');
            navigate(`/math-test?${params}`);
        },
        [navigate, mathDifficulty, studentId],
    );

    const startSmartDrill = useCallback(async () => {
        setSmartDrillBusy(true);
        setSmartDrillHint('');
        try {
            const fromSummary = pickSmartVerbalDomain(summary);
            const skill = fromSummary || (await fetchWeakestVerbalSkill());
            if (!skill) {
                                setSmartDrillHint(
                                    'Not enough data yet. Solve at least one question and try again.',
                                );
                return;
            }
            const title = categoryTitleForKey(skill) || skill;
            beginSectionTest(skill, title, 'foundation', true);
        } catch {
            setSmartDrillHint('Network error. Try again.');
        } finally {
            setSmartDrillBusy(false);
        }
    }, [beginSectionTest, summary]);

    const startMathSmartDrill = useCallback(() => {
        setMathSmartBusy(true);
        setMathSmartHint('');
        const domain = pickSmartMathDomain(summary);
        const valid: MathDomain[] = ['algebra', 'advanced_math', 'psda', 'geometry'];
        const next = valid.includes(domain as MathDomain) ? domain : 'algebra';
        const label =
            summary && !summary.isNewStudent
                ? `Weakest domain: ${summary.weakest_module === 'math' ? summary.weakest_domain_label : next}`
                : 'Starting with Algebra (not enough data yet).';
        setMathSmartHint(label);
        beginMathSection(next, true);
        setMathSmartBusy(false);
    }, [beginMathSection, summary]);

    const displayName = user ? displayNameForUser(user) : '';
    const initial = user ? avatarInitialForUser(user) : '?';
    const showPicture = Boolean(user?.picture && !avatarFailed);
    const verbalDomains = domainsForModule(summary, 'verbal');
    const mathDomains = domainsForModule(summary, 'math');
    const verbalTotals = verbalDomains.reduce(
        (acc, row) => ({ attempts: acc.attempts + row.attempts, correct: acc.correct + row.correct }),
        { attempts: 0, correct: 0 },
    );
    const mathTotals = mathDomains.reduce(
        (acc, row) => ({ attempts: acc.attempts + row.attempts, correct: acc.correct + row.correct }),
        { attempts: 0, correct: 0 },
    );
    const verbalAccuracy =
        verbalTotals.attempts > 0
            ? Math.round((verbalTotals.correct / verbalTotals.attempts) * 100)
            : null;
    const mathAccuracy =
        mathTotals.attempts > 0
            ? Math.round((mathTotals.correct / mathTotals.attempts) * 100)
            : null;
    const recentSessions = summary?.lastSessions ?? [];
    const verbalSessions = recentSessions
        .filter((session) => session.moduleType === 'verbal')
        .slice(0, 5);
    const mathSessions = recentSessions
        .filter((session) => session.moduleType === 'math')
        .slice(0, 5);

    return (
        <div className="app-shell app-shell--hub app-shell--dashboard w-full min-h-screen">
            <div className="app-hub-dashboard w-full max-w-6xl mx-auto px-4 py-8">
                {user ? (
                    <header className="app-hub-profile-bar flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full" aria-label="User profile">
                        <div className="app-hub-profile-identity">
                            {showPicture ? (
                                <img
                                    className="app-hub-profile-avatar"
                                    src={user.picture!}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    onError={() => setAvatarFailed(true)}
                                />
                            ) : (
                                <span className="app-hub-profile-avatar app-hub-profile-avatar--fallback" aria-hidden>
                                    {initial}
                                </span>
                            )}
                            <div className="app-hub-profile-text">
                                <span className="app-hub-profile-name">{displayName}</span>
                                {user.name && user.email !== displayName ? (
                                    <span className="app-hub-profile-email">{user.email}</span>
                                ) : null}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="app-hub-exit-btn w-full sm:w-auto"
                            onClick={handleLogout}
                        >
                            Выйти / Exit
                        </button>
                    </header>
                ) : null}

                {/* Verbatim MadCrab practice-test-cta */}
                <div className="practice-test-cta flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 w-full">
                    <p className="practice-test-cta__text">
                        Ready for the real thing? Start an adaptive two-module practice exam.
                    </p>
                    <div className="flex w-full sm:w-auto gap-3">
                        <span className="w-full sm:w-auto" title="Full Practice Test mode is coming soon!">
                            <button
                                type="button"
                                className="practice-test-cta__btn w-full cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500 opacity-60"
                                disabled
                            >
                                Start Full Practice Test (Coming soon)
                            </button>
                        </span>
                    </div>
                </div>

                <div className="dashboard-grid grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
                    {/* ── Verbal card: EXACT MadCrab_V App.jsx hub markup ── */}
                    <div className="dashboard-column w-full">
                        <div className="app-hub-card app-hub-card--wide w-full">
                            <p className="app-hub-brand">SAT Generator</p>
                            <h2 className="app-hub-title">Reading &amp; Writing — секции</h2>
                            <p className="app-hub-hint">
                                Выберите уровень сложности и домен Digital SAT Suite. Вопросы
                                генерируются для выбранной секции (смесь навыков по спецификации
                                College Board).
                            </p>
                            {activeSession?.moduleType === 'verbal' ? (
                                <ActiveSessionBanner
                                    session={activeSession}
                                    onResume={resumeDraft}
                                    onReset={() => void resetDraft()}
                                />
                            ) : null}

                            <DomainAccuracyList
                                title="Domain accuracy"
                                overallAccuracy={verbalAccuracy}
                                totalQuestions={verbalTotals.attempts}
                                domains={verbalDomains}
                            />

                            <label className="app-hub-field-label" htmlFor="hub-difficulty-rw">
                                Сложность
                            </label>
                            <select
                                id="hub-difficulty-rw"
                                className="app-hub-select"
                                value={rwDifficulty}
                                onChange={(e) => setRwDifficulty(e.target.value)}
                            >
                                <option value="foundation">{DIFFICULTY_LABEL.easy}</option>
                                <option value="advanced">{DIFFICULTY_LABEL.medium}</option>
                            </select>

                            <p className="app-hub-sections-label">Секция</p>
                            <div className="app-hub-smart-drill">
                                <button
                                    type="button"
                                    className="app-hub-section app-hub-section--smart w-full"
                                    onClick={() => void startSmartDrill()}
                                    disabled={smartDrillBusy}
                                >
                                    {smartDrillBusy
                                        ? 'Загрузка…'
                                        : 'Умная тренировка — слабая категория'}
                                </button>
                                {smartDrillHint ? (
                                    <p className="app-hub-smart-hint">{smartDrillHint}</p>
                                ) : null}
                            </div>
                            <HubRwCategoryCards
                                rwDifficulty={rwDifficulty}
                                savedSession={null}
                                beginSectionTest={beginSectionTest}
                            />
                            <DashboardRecentSessions
                                title="Recent Reading & Writing Sessions"
                                sessions={verbalSessions}
                                onDeleteSession={handleDeleteSession}
                            />
                        </div>
                    </div>

                    {/* ── Math card: literal duplicate of Verbal card structure ── */}
                    <div className="dashboard-column w-full">
                        <div className="app-hub-card app-hub-card--wide w-full">
                            <p className="app-hub-brand">SAT Generator</p>
                            <h2 className="app-hub-title">Math — секции</h2>
                            <p className="app-hub-hint">
                                Выберите уровень сложности и домен Digital SAT Suite. Вопросы
                                генерируются для выбранной секции (смесь навыков по спецификации
                                College Board).
                            </p>
                            {activeSession?.moduleType === 'math' ? (
                                <ActiveSessionBanner
                                    session={activeSession}
                                    onResume={resumeDraft}
                                    onReset={() => void resetDraft()}
                                />
                            ) : null}

                            <DomainAccuracyList
                                title="Domain accuracy"
                                overallAccuracy={mathAccuracy}
                                totalQuestions={mathTotals.attempts}
                                domains={mathDomains}
                            />

                            <label className="app-hub-field-label" htmlFor="hub-difficulty-math">
                                Сложность
                            </label>
                            <select
                                id="hub-difficulty-math"
                                className="app-hub-select"
                                value={mathDifficulty}
                                onChange={(e) => setMathDifficulty(e.target.value)}
                            >
                                <option value="foundation">{DIFFICULTY_LABEL.easy}</option>
                                <option value="advanced">{DIFFICULTY_LABEL.medium}</option>
                            </select>

                            <p className="app-hub-sections-label">Секция</p>
                            <div className="app-hub-smart-drill">
                                <button
                                    type="button"
                                    className="app-hub-section app-hub-section--smart w-full"
                                    onClick={startMathSmartDrill}
                                    disabled={mathSmartBusy}
                                >
                                    {mathSmartBusy
                                        ? 'Загрузка…'
                                        : 'Умная тренировка — слабые категории'}
                                </button>
                                {mathSmartHint ? (
                                    <p className="app-hub-smart-hint">{mathSmartHint}</p>
                                ) : null}
                            </div>
                            <div className="app-hub-section-grid">
                                {MATH_SECTIONS.filter((s) => s.id !== 'smart_practice').map(
                                    (section) => (
                                        <div
                                            key={section.id}
                                            className="app-hub-section--split"
                                        >
                                            <div className="app-hub-section__left">
                                                <button
                                                    type="button"
                                                    className="app-hub-category-btn w-full"
                                                    onClick={() => beginMathSection(section.id)}
                                                >
                                                    {section.title}
                                                </button>
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>
                            <DashboardRecentSessions
                                title="Recent Math Sessions"
                                sessions={mathSessions}
                                onDeleteSession={handleDeleteSession}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}